import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Button as AntButton, Checkbox, Tooltip} from "antd";
import Button from "@common/Button";
import Tag from "@common/Tag";
import {
    Add, ArrowDownwardRounded, ArrowUpwardRounded,
    CheckRounded,
    CloseRounded,
    KeyboardArrowDownRounded,
    CalculateOutlined
} from "@mui/icons-material";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";
import {formatQuestionAnswerTime} from "@common/utilityFunctions";
import GroupInstructions from "./GroupInstructions";
import PopConfirm from "@common/PopConfirm";

const TestQuestionCard = ({index, originalSeq, expanded = false, checkboxChecked, test, answerTime, nonCalculator = null, section = null, concept, initialIndex, questionTotal, testDiffLevel, questionAnswerTypeGroupDisplay = 'Put answer type here', maxMark = 1, editTestQuestion, formattedInstructions, selectedForTest = false, inQuestionBank = true, onAdd, onRemove, onMoveQuestion, handleCheckboxChange, handleExpandChange, questionType, questionId = null, questionLevelNames = [], ...rest }) => {

    const [maxMarkState, setMaxMarkState] = useState(maxMark);
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);

    const handleAdd = () => {
        onAdd?.(questionId);
    }
    const handleRemove = () => {
        onRemove?.(questionId);
    }
    const handleMoveUp = () => {
        onMoveQuestion?.(index,index-1);
    }
    const handleMoveDown = () => {
        onMoveQuestion?.(index,index+1);
    }

    const handleEditTestQuestion = () => {
        if(user.showResourceIds){
            editTestQuestion?.({questionId,...rest});
        }
    }

    const onCheckboxChange = (event) => {
        handleCheckboxChange?.(!event.target.checked, questionId);
    }
    const onExpandChange = () => {
        handleExpandChange?.(!expanded, questionId);
    }

    // ADDED: Helper for keyboard interaction on the CMS Edit Tag
    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
        }
    }

    return (
        <div className={'ch-test-editor-card'+ ((inQuestionBank || originalSeq === initialIndex) ? '' : ' unsaved-order')}>
            {(!inQuestionBank) &&
                <Checkbox
                    onChange={onCheckboxChange}
                    checked={checkboxChecked}
                    aria-label={checkboxChecked ? 'Deselect question' : 'Select question'}
                />
            }
            <div className={'ch-test-editor-card-question'}>
                <div className={'ch-test-editor-card-result'}>
                    {(!inQuestionBank) &&
                        <AntButton shape={'circle'} onClick={handleMoveUp} aria-label={`Move question ${initialIndex + 1} up`} type={'text'} disabled={index === 0}>
                            <ArrowUpwardRounded aria-hidden="true" />
                        </AntButton>
                    }
                    <span className={'ch-test-editor-card-result-number'}>
                        {initialIndex === undefined ? '' : (initialIndex + 1)}.
                    </span>
                    {(!inQuestionBank) &&
                        <AntButton shape={'circle'} aria-label={`Move question ${initialIndex + 1} down`} onClick={handleMoveDown} type={'text'} disabled={index === questionTotal-1}>
                            <ArrowDownwardRounded aria-hidden="true" />
                        </AntButton>
                    }
                </div>
                <div>
                    <div className={'flexRow flexSpaceBetween'}>
                        {formattedInstructions ? (<MathJaxHTMLContent html={formattedInstructions} disableInputs={true} className={`ch-test-editor-card-question-content ${expanded ? 'expanded' : ''}`}/>)
                            :(questionType !== 'GROUP_QUESTION' ? 'Formatted instructions missing' : null)}
                        {questionType === 'GROUP_QUESTION' && <GroupInstructions getMaxMark={(val)=>{setMaxMarkState(val)}} questionData={{...rest}} className={`ch-test-editor-card-question-content ${expanded ? 'expanded' : ''}`} />}
                        <AntButton
                            type={'link'}
                            onClick={onExpandChange}
                            style={{
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                            }}
                            aria-label={expanded ? `Collapse question ${initialIndex + 1} content` : `Expand question ${initialIndex + 1} content`}
                            aria-expanded={expanded}
                        >
                            <KeyboardArrowDownRounded aria-hidden="true" />
                        </AntButton>
                    </div>
                    <div className={'ch-test-editor-card-tags flexColumn smallGap'}>
                        <div className={'flexWrap smallGap'}>
                            {concept && concept.conceptName &&
                                <Tooltip title={<MathJaxHTMLContent html={concept.conceptName}/>}>
                                    <Tag color={'green'}>
                                        <MathJaxHTMLContent style={{maxWidth:'120px',overflow:'hidden', textOverflow:'ellipsis'}} html={concept.conceptName}/>
                                    </Tag>
                                </Tooltip>
                            }

                            {rest.skillName &&
                                <Tooltip title={rest.skillName}>
                                    <Tag style={{maxWidth:'120px',overflow:'hidden', textOverflow:'ellipsis'}} color={'gold'} tabIndex={0}>
                                        {rest.skillName}
                                    </Tag>
                                </Tooltip>
                            }
                            <Tag color={'blue'}>{questionAnswerTypeGroupDisplay}</Tag>
                            <Tag color={'purple'}>{maxMarkState} Mark</Tag>
                            {answerTime && <Tag color={'gold'}>{formatQuestionAnswerTime(answerTime)}</Tag>}
                            <Tag color={'volcano'}>{questionLevelNames.length > 0 ? questionLevelNames[testDiffLevel-1] : 'Level '+testDiffLevel}</Tag>
                            {section !== null && <Tag color={'green'}>{'Section '+section}</Tag>}
                            {test.diagnosticTest && <Tag color={'magenta'}>{rest.skillContentDescriptor}</Tag>}

                            {(user.showResourceIds && user.adminUser) &&
                                <PopConfirm placement="top" title={'Edit Question in CMS?'} onConfirm={handleEditTestQuestion}>
                                    <Tag
                                        style={{cursor:'pointer'}}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Edit question ID ${questionId} in CMS`}
                                        onKeyDown={handleTagKeyDown}
                                    >
                                        Id #{questionId}
                                    </Tag>
                                </PopConfirm>
                            }

                            {(user.showResourceIds && !user.adminUser) && <Tag>Id #{questionId}</Tag>}

                            {((nonCalculator !== null && !nonCalculator) && (test.exam || test.diagnosticTest)) &&
                                <Tooltip title={'Calculator question'}>
                                    {/* Ensure the icon Wrapper inside Tooltip can receive focus if it's purely icon based */}
                                    <span className={'calculator-question-icon'} tabIndex={0} aria-label="Calculator question">
                                        <CalculateOutlined aria-hidden="true" style={{color:'var(--primary-colour)'}}/>
                                    </span>
                                </Tooltip>
                            }

                            {test.exam && <span className={'ch-sml-font'}>[{rest.examCitations}]</span>}
                        </div>
                    </div>
                </div>
            </div>
            <div className={'ch-test-editor-card-actions'}>
                {(!selectedForTest && inQuestionBank) &&
                    <Button className={'js-add-question-btn'} icon={<Add/>} aria-label={`Add question to ${brandConfig.testName}`} onClick={handleAdd} type={'outline'}/>
                }
                {(selectedForTest && inQuestionBank) &&
                    <Button icon={<CheckRounded/>} aria-label={'Question is selected'} type={'outline'} disabled={true}/>
                }
                {(selectedForTest && !inQuestionBank) &&
                    <Button className="js-remove-question-btn" icon={<CloseRounded/>} aria-label={`Remove question from ${brandConfig.testName}`} onClick={handleRemove} type={'outline'}/>
                }
            </div>
        </div>
    );
};

export default TestQuestionCard;